/* stylelint-disable no-descending-specificity */
import styled, { css } from "styled-components";
import { colors } from "@a0/quantum-web";

const hoverStyle = css`
  text-decoration: none;
  p {
    transition: all 0.2s ease-in-out;
  }
  p:first-of-type {
    position: relative;
    display: inline-block;
    :after {
      content: "→";
      padding-left: 0.5rem;
      position: relative;
      opacity: 0;
      transition: all 0.1s ease;
    }
  }
  :hover {
    p:last-of-type {
      text-decoration: none;
      color: ${colors.neutral_n900};
    }
    p:first-of-type:after {
      opacity: 1;
    }
  }
`;

export const Wrapper = styled.section`
  max-width: 126.4rem;
  margin: 0 auto;
  padding: 0;
  margin-bottom: 6.4rem;

  ${({ theme }) => theme.quantum.media.tablet`  
  `}

  ${({ theme }) => theme.quantum.media.desktop`  
    margin-bottom: 8rem;
  `}
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 1.6rem;
  margin-bottom: 6.4rem;

  ${({ theme }) => theme.quantum.media.tablet`  
    display: grid;
    grid-template-areas: 
      'events ambassadors'
      'events apollo';
    grid-gap: 2.4rem;
    grid-template-rows: min-content minmax(24rem, 1fr);
    grid-template-columns: repeat(12, 1fr);
  `}

  ${({ theme }) => theme.quantum.media.desktop`  
    grid-gap: 3.2rem;
  `}
`;

export const EventsWrapper = styled.div`
  padding: 2.4rem;
  background: ${colors.neutral_n60};
  grid-area: events;
  border-radius: 1.6rem;

  ${({ theme }) => theme.quantum.media.tablet`  
    padding: 3.2rem 2.4rem;
    border-radius: 2.4rem;
    grid-column: 1 / 9;
  `}

  ${({ theme }) => theme.quantum.media.desktop`  
    padding: 3.2rem;
  `}
`;

export const Title = styled.p`
  font-family: "Space Grotesk", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 1.4rem;
  line-height: 2rem;
  letter-spacing: 0.15rem;
  text-transform: uppercase;
  color: ${({ light }) => (light ? colors.neutral_white : colors.neutral_n900)};
  border-bottom: 0.05rem solid ${colors.neutral_n100};
  padding-bottom: 1.6rem;
  margin: 0;
`;

export const EventsContainer = styled.div`
  padding: 2.4rem 0;
  border-bottom: 0.05rem solid ${colors.neutral_n100};

  ${({ theme }) => theme.quantum.media.tablet`  
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    column-gap: 2.4rem;
  `}

  ${({ theme }) => theme.quantum.media.desktop`  
    padding-bottom: 4.8rem;
    column-gap: 3.2rem;
  `}
`;

export const BigEventContainer = styled.a`
  padding-bottom: 0.8rem;
  border-bottom: 0.05rem solid ${colors.neutral_n100};
  margin-bottom: 1.6rem;
  display: grid;
  text-decoration: none;
  grid-column: 1 / 4;
  row-gap: 1.2rem;

  ${hoverStyle}

  ${({ theme }) => theme.quantum.media.tablet`
    margin: 0;
    padding: 0.5rem;
    border: none;
    row-gap: unset;
    img {
      margin-bottom: 2.4rem;
    }
  `}
`;

export const EventImage = styled.figure`
  width: 100%;
  aspect-ratio: 4 / 2;
  border-radius: 0.8rem;
  position: relative;
  height: 100%;
  overflow: hidden;
  margin: 0 0 1.6rem;

  ${({ theme }) => theme.quantum.media.tablet`
    height: 13.5rem;
    aspect-ratio: auto;
    margin-bottom: 2.4rem;
  `}

  ${({ theme }) => theme.quantum.media.desktop`
    height: 18rem;
    border-radius: 1.6rem;
  `}
`;

export const EventListsWrapper = styled.div`
  grid-column: 5 / 9;
`;

export const EventContainer = styled.a`
  display: block;
  text-decoration: none;
  cursor: pointer;

  ${hoverStyle}

  + a {
    padding-top: 1.6rem;
    margin-top: 1.6rem;
    border-top: 0.05rem solid ${colors.neutral_n100};
  }
`;

export const ViewMoreEventsWrapper = styled.div`
  padding-top: 1.6rem;

  a {
    color: ${colors.neutral_n900};
    :hover {
      color: ${colors.neutral_n800};
      text-decoration: underline;
      text-underline-offset: 0.4rem;
    }
  }
`;

export const AmbassadorsWrapper = styled.div`
  padding: 2.4rem;
  background: ${colors.neutral_n800};
  border-radius: 1.6rem;

  a {
    color: ${colors.neutral_white};
    padding-top: 1.6rem;
    :hover {
      color: ${colors.neutral_n60};
    }

    :nth-child(3):hover,
    :nth-child(4):hover {
      text-decoration: underline;
      text-underline-offset: 0.4rem;
    }
    :nth-child(3) {
      padding: 1.6rem 0;
      border-bottom: 0.05rem solid ${colors.neutral_n500};
    }
  }

  ${({ theme }) => theme.quantum.media.tablet`  
    border-radius: 2.4rem;
    grid-column: 9 / 13;
  `}

  ${({ theme }) => theme.quantum.media.desktop`  
    padding: 3.2rem;
  `}
`;

export const AmbassadorContainer = styled.a`
  display: grid;
  grid-template-areas: /* stylelint-disable-line declaration-colon-space-after */
    "icon ."
    "icon .";
  grid-gap: 0.4rem 2.4rem;
  grid-template-columns: 3.2rem auto;
  padding: 1.6rem 0;
  border-bottom: 0.05rem solid ${colors.neutral_n500};
  transition: all 0.2s ease-in-out;
  justify-content: flex-start;

  p {
    color: ${colors.neutral_n60};
    margin: 0;
  }

  p:nth-child(2) {
    color: ${colors.neutral_white};
  }

  p:nth-child(3) {
    text-transform: capitalize;
  }

  ${hoverStyle}

  :hover {
    text-decoration: none;
    p:nth-child(3) {
      color: ${colors.neutral_white};
    }
  }

  ${({ theme }) => theme.quantum.media.desktop`  
    grid-gap: 0.4rem 3.2rem;
    grid-template-columns: 4rem auto;
    img {
      max-width: 4rem;
    }
  `}
`;

export const AmbassadorImg = styled.figure`
  grid-area: icon;
  height: 3.2rem;
  width: 3.2rem;
  border-radius: 0.4rem;
  overflow: hidden;
  position: relative;
  margin: 0;

  img {
    aspect-ratio: 1 / 1;
    object-fit: cover;
  }

  ${({ theme }) => theme.quantum.media.desktop`  
    width: 4rem;
    height: 4rem;
    border-radius: 0.8rem;
  `}
`;

export const FlagIcon = styled.img`
  border-radius: 0.4rem;
  height: 1.6rem;
  margin-right: 0.8rem;
  vertical-align: middle;
`;

export const ApolloProgramWrapper = styled.a`
  padding: 2.4rem;
  background: ${colors.neutral_n800};
  border-radius: 1.6rem;
  display: grid;
  grid-template-areas: /* stylelint-disable-line declaration-colon-space-after */
    "icon ."
    "icon .";
  grid-gap: 0.4rem 1.6rem;
  cursor: pointer;
  justify-content: flex-start;

  ${hoverStyle};

  :hover {
    p:last-of-type {
      text-decoration: none;
      color: ${colors.neutral_white};
    }
  }

  ${({ theme }) => theme.quantum.media.tablet`  
    padding: 3.2rem;
    border-radius: 2.4rem;
    grid-template-rows: 2.4rem auto;
    align-self: flex-start;
    grid-column: 9 / 13;
  `}

  ${({ theme }) => theme.quantum.media.desktop`  
    grid-gap: 0.4rem 2.4rem;
    grid-template-rows: auto auto;
  `}
`;

export const ApolloIcon = styled.img`
  grid-area: icon;
`;

export const CommunityContainer = styled.div``;

export const Overline = styled.p`
  font-family: "SpaceGrotesk", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 1.4rem;
  line-height: 2rem;
  letter-spacing: 0.12rem;
  text-transform: uppercase;
  color: ${colors.neutral_n60};
  padding-bottom: 1.6rem;
  border-bottom: 0.1rem solid ${colors.neutral_n800};
  margin: 0 0 3.2rem;
`;

export const CommunityLinksWrapper = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-gap: 3.2rem;

  ${({ theme }) => theme.quantum.media.tablet`  
    grid-template-columns: repeat(2, 1fr);
    gap: 2.4rem;
  `}

  ${({ theme }) => theme.quantum.media.desktop`  
    gap: 3.2rem;
  `}
`;

export const CommunityItem = styled.li`
  p:first-of-type {
    :after {
      content: "→";
      position: absolute;
      margin-left: 0.6rem;
      opacity: 0;
      transition: all 0.1s ease;
    }
  }
  :hover {
    p:first-of-type {
      :after {
        opacity: 1;
      }
    }
  }
`;

export const CommunityLink = styled.a`
  text-decoration: none;
  display: flex;
  align-items: flex-start;
  gap: 0.4rem 2.4rem;
  justify-content: flex-start;

  span:first-of-type {
    grid-area: icon;
  }

  ${hoverStyle};

  :hover {
    p:last-of-type {
      text-decoration: none;
      color: ${colors.neutral_white};
    }
  }
`;

export const CommunityLinkContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
`;
